import { PapiGallection } from '@wix/da-papi-types';
import { SCRAPS_FOLDER_ID } from '@wix/da-url';

export interface GalleryHierarchy {
  sortedGalleries: PapiGallection[];
  depthMap: Map<number, number>;
}

/**
 * Get Featured folder ID by finding folder named "Featured" or using first non-Scraps folder.
 * Scraps folder is excluded as it's a special system folder.
 */
export function getFeaturedFolderId(
  galleries: PapiGallection[]
): number | undefined {
  const featured = galleries.find(g => g.name === 'Featured');
  if (featured) return featured.folderId;

  // Fall back to first non-Scraps folder
  const firstNonScraps = galleries.find(g => g.folderId !== SCRAPS_FOLDER_ID);
  return firstNonScraps?.folderId;
}

/**
 * Build gallery hierarchy for display in MultiSelectField.
 * Creates parent-child relationships and calculates indentation depths.
 * Featured folder is determined by name or defaults to first folder.
 */
export function buildGalleryHierarchy(
  galleries: PapiGallection[]
): GalleryHierarchy {
  const featuredFolderId = getFeaturedFolderId(galleries);

  const galleryMap = new Map(galleries.map(g => [g.folderId, g]));
  const childrenMap = new Map<number, PapiGallection[]>();
  const depthMap = new Map<number, number>();

  // Build children map
  galleries.forEach(gallery => {
    const parentId = gallery.parentId ?? 0;
    const children = childrenMap.get(parentId);
    if (children) {
      children.push(gallery);
    } else {
      childrenMap.set(parentId, [gallery]);
    }
  });

  // Sort children arrays once
  childrenMap.forEach(children => {
    children.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Calculate depths in single pass
  const calculateDepth = (folderId: number): number => {
    const cachedDepth = depthMap.get(folderId);
    if (cachedDepth !== undefined) {
      return cachedDepth;
    }
    const gallery = galleryMap.get(folderId);
    if (!gallery?.parentId) {
      depthMap.set(folderId, 0);
      return 0;
    }
    const depth = calculateDepth(gallery.parentId) + 1;
    depthMap.set(folderId, depth);
    return depth;
  };

  galleries.forEach(g => calculateDepth(g.folderId));

  // Build sorted list with hierarchy
  const sortedGalleries: PapiGallection[] = [];
  const addGalleryWithChildren = (gallery: PapiGallection) => {
    sortedGalleries.push(gallery);
    const children = childrenMap.get(gallery.folderId) || [];
    children.forEach(child => addGalleryWithChildren(child));
  };

  // First add Featured folder
  const featured = galleries.find(g => g.folderId === featuredFolderId);
  if (featured) {
    addGalleryWithChildren(featured);
  }

  // Then add root-level galleries (parentId is null or 0) that aren't featured
  const rootGalleries = childrenMap.get(0) || [];
  rootGalleries
    .filter(g => g.folderId !== featuredFolderId)
    .forEach(gallery => addGalleryWithChildren(gallery));

  return { sortedGalleries, depthMap };
}
